// src/components/Navigation.jsx
import React, { useEffect, useState, useCallback, useRef } from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import styles from "./modules/Navigation.module.css";
import { useT, useI18nReady } from "./components/TextProvider";

/** ===== helpers: auth storage ===== */
function getStoredUser() {
  const ls = window.localStorage.getItem("auth:user");
  const ss = window.sessionStorage.getItem("auth:user");
  const raw = ls ?? ss;
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}
function clearAuthStorage() {
  const clear = (storage) => {
    const keys = [];
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i);
      if (k && k.startsWith("auth:")) keys.push(k);
    }
    keys.forEach((k) => storage.removeItem(k));
  };
  clear(window.localStorage);
  clear(window.sessionStorage);
}
function getAnyTokenFromStorage() {
  const pick = (s) =>
    s.getItem("auth:access") ||
    s.getItem("auth:token") ||
    s.getItem("auth:key") ||
    s.getItem("auth:jwt") ||
    s.getItem("auth:auth_token") ||
    null;
  return pick(window.localStorage) || pick(window.sessionStorage);
}
function makeAuthHeader(tokenLike) {
  if (!tokenLike) return {};
  const token = String(tokenLike);
  const forcedScheme = process.env.REACT_APP_AUTH_SCHEME; // "Bearer" | "Token" | "JWT"
  if (forcedScheme) return { Authorization: `${forcedScheme} ${token}` };
  const looksLikeJwt = token.startsWith("eyJ"); // эвристика
  return looksLikeJwt ? { Authorization: `Bearer ${token}` } : { Authorization: `Token ${token}` };
}

/** ===== helpers: user visuals ===== */
function getAvatarUrl(user) {
  return user?.img || user?.photo || user?.avatar || null;
}
function getUserInitial(user) {
  const base = user?.username || user?.name || user?.email || user?.phone || user?.id || "";
  const ch = String(base).trim().charAt(0);
  return ch ? ch.toUpperCase() : "?";
}

/** ===== consts ===== */
const USER_ROLES_ENDPOINT =
  process.env.REACT_APP_USER_ROLES_ENDPOINT ||
  "https://apitr.subxonqulov.uz/api/user-roles/";

// Роуты для тренера — подставьте свои при желании
const TRAINER_ADD_ROUTE = "/TrainerCreate";
const TRAINER_LIST_ROUTE = "/TrainerMy";
const DESC_LIST_ROUTE = "/MyDescriptions";
const NEW_SUBJECT_ROUTE = "/YangiFan";
const MY_SUBJECTS_ROUTE = "/MeningFanlarim";
const MY_WRITTEN_QUESTIONS_ROUTE = "/MyQuestions";

/**
 * Navigation with profile dropdown & trainer role
 */
export const Navigation = () => {
  const t = useT();
  const ready = useI18nReady();

  const [open, setOpen] = useState(false);          // burger
  const [menuOpen, setMenuOpen] = useState(false);  // profile dropdown
  const [user, setUser] = useState(() => getStoredUser());
  const [isTrainer, setIsTrainer] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  /** Закрытие панелей при роутинге */
  useEffect(() => {
    setOpen(false);
    setMenuOpen(false);
  }, [location.pathname]);

  /** Реакция на изменения auth-состояния */
  useEffect(() => {
    const onStorage = (e) => {
      if (!e.key || e.key.startsWith("auth:")) {
        setUser(getStoredUser());
      }
    };
    const onAuthChanged = () => setUser(getStoredUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth:changed", onAuthChanged);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth:changed", onAuthChanged);
    };
  }, []);

  /** Проверка роли тренера */
  useEffect(() => {
    let abort = false;

    async function checkTrainer() {
      if (!user?.id) { setIsTrainer(false); return; }
      try {
        const token = getAnyTokenFromStorage();
        const res = await fetch(USER_ROLES_ENDPOINT, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token ? makeAuthHeader(token) : {}),
          },
          credentials: "include",
        });
        if (!res.ok) { setIsTrainer(false); return; }
        const list = await res.json().catch(() => []);
        // Ожидаемый формат: [ { id, user, role }, ... ]
        const mine = Array.isArray(list) ? list.some(r => Number(r?.user) === Number(user.id) && Number(r?.role) === 2) : false;
        if (!abort) setIsTrainer(mine);
      } catch {
        if (!abort) setIsTrainer(false);
      }
    }

    checkTrainer();
    return () => { abort = true; };
  }, [user?.id]);

  /** Проверка роли администратора */
  useEffect(() => {
    let abort = false;

    async function checkAdmin() {
      if (!user?.id) { setIsAdmin(false); return; }
      try {
        const token = getAnyTokenFromStorage();
        const res = await fetch(USER_ROLES_ENDPOINT, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token ? makeAuthHeader(token) : {}),
          },
          credentials: "include",
        });
        if (!res.ok) { setIsAdmin(false); return; }
        const list = await res.json().catch(() => []);
        // Ожидаемый формат: [ { id, user, role }, ... ]
        const mine = Array.isArray(list) ? list.some(r => Number(r?.user) === Number(user.id) && Number(r?.role) === 3) : false;
        if (!abort) setIsAdmin(mine);
      } catch {
        if (!abort) setIsAdmin(false);
      }
    }

    checkAdmin();
    return () => { abort = true; };
  }, [user?.id]);

  /** Esc закрывает всё */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        setMenuOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /** Клик вне burger-панели */
  const panelRef = useCallback((node) => {
    if (!node) return;
    const handleClick = (e) => { if (!node.contains(e.target)) setOpen(false); };
    const id = setTimeout(() => document.addEventListener("click", handleClick), 0);
    return () => { clearTimeout(id); document.removeEventListener("click", handleClick); };
  }, []);

  /** Клик вне dropdown */
  const dropdownRef = useRef(null);
  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [menuOpen]);

  const linkClass = ({ isActive }) =>
    [styles.link, isActive ? styles.linkActive : ""].join(" ").trim();
  const mobileLinkClass = ({ isActive }) =>
    [styles.mobileLink, isActive ? styles.mobileActive : ""].join(" ").trim();

  const avatarUrl = getAvatarUrl(user);
  const initial = getUserInitial(user);

  function handleLogout() {
    clearAuthStorage();
    window.dispatchEvent(new Event("auth:changed"));
    setMenuOpen(false);
    navigate("/login", { replace: true });
  }

  return (
    <header className={styles.header}>
      {/* Left */}
      <nav className={styles.navLeft} aria-label="Primary navigation">
        <NavLink to="/about" className={linkClass}>{t('Biz haqimizda')}</NavLink>
      </nav>

      {/* Center brand */}
      <Link to="/" className={styles.navCenter} aria-label="Home">
        {t('TRAIN AI')}
      </Link>

      {/* Right */}
      <nav className={styles.navRight} aria-label="Secondary navigation">
        {/* <NavLink to="/blog" className={linkClass}>Blog</NavLink> */}

        {!user ? (
          <NavLink to="/login" className={linkClass}>Kirish</NavLink>
        ) : (
          <div className={styles.profileWrap} ref={dropdownRef}>
            <button
              type="button"
              className={styles.avatarBtn}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(v => !v)}
              title={user?.username || "Account"}
            >
              {avatarUrl ? (
                <img src={avatarUrl} alt="User avatar" className={styles.avatarImg} referrerPolicy="no-referrer" />
              ) : (
                <span className={styles.avatarCircle} aria-hidden="true">{initial}</span>
              )}
            </button>

            {menuOpen && (
              <div className={styles.dropdown} role="menu" aria-label="User menu">
                {/* 1. Username */}
                <div className={styles.dropdownUser} role="presentation">
                  {user?.username || user?.email || "User"}
                </div>
                {/* 2. Профиль - не показываем для администраторов */}
                {!isAdmin && (
                  <Link to="/profile" className={styles.dropdownItem} role="menuitem" onClick={() => setMenuOpen(false)}>
                    {t('profile')}
                  </Link>
                )}
                {/* --- Вставка для тренеров --- */}
                {isTrainer && (
                  <>
                    <Link
                      to={TRAINER_ADD_ROUTE}
                      className={styles.dropdownItem}
                      role="menuitem"
                      onClick={() => setMenuOpen(false)}
                    >
                      {t('addTren')}
                    </Link>
                    <Link
                      to={TRAINER_LIST_ROUTE}
                      className={styles.dropdownItem}
                      role="menuitem"
                      onClick={() => setMenuOpen(false)}
                    >
                      {t('myTren')}
                    </Link>
                    <Link
                      to={MY_WRITTEN_QUESTIONS_ROUTE}
                      className={styles.dropdownItem}
                      role="menuitem"
                      onClick={() => setMenuOpen(false)}
                    >
                      Yozma savollarim
                    </Link>
                    {/* <Link
                      to={DESC_LIST_ROUTE}
                      className={styles.dropdownItem}
                      role="menuitem"
                      onClick={() => setMenuOpen(false)}
                    >
                      {t('myDesc')}
                    </Link> */}
                    <Link
                      to={NEW_SUBJECT_ROUTE}
                      className={styles.dropdownItem}
                      role="menuitem"
                      onClick={() => setMenuOpen(false)}
                    >
                      {t('Yangi fan')}
                    </Link>
                    <Link
                      to={MY_SUBJECTS_ROUTE}
                      className={styles.dropdownItem}
                      role="menuitem"
                      onClick={() => setMenuOpen(false)}
                    >
                      {t('Mening fanlarim')}
                    </Link>
                  </>
                )}
                {/* --- Вставка для администраторов --- */}
                {isAdmin && (
                  <>
                    <Link
                      to={TRAINER_LIST_ROUTE}
                      className={styles.dropdownItem}
                      role="menuitem"
                      onClick={() => setMenuOpen(false)}
                    >
                      {t('Mashqlar')}
                    </Link>
                    <Link
                      to={MY_WRITTEN_QUESTIONS_ROUTE}
                      className={styles.dropdownItem}
                      role="menuitem"
                      onClick={() => setMenuOpen(false)}
                    >
                      {t('Yozma savollar')}
                    </Link>
                    <Link
                      to={MY_SUBJECTS_ROUTE}
                      className={styles.dropdownItem}
                      role="menuitem"
                      onClick={() => setMenuOpen(false)}
                    >
                      {t('Fanlar')}
                    </Link>
                    <Link
                      to="/user-roles"
                      className={styles.dropdownItem}
                      role="menuitem"
                      onClick={() => setMenuOpen(false)}
                    >
                      Foydalanuvchi rollari
                    </Link>
                  </>
                )}
                {/* 3. Выйти */}
                <button type="button" className={styles.dropdownItem} role="menuitem" onClick={handleLogout}>
                  {t('Logout')}
                </button>
              </div>
            )}
          </div>
        )}
      </nav>

      {/* Burger */}
      <button
        className={[styles.burger, open ? styles.burgerOpen : ""].join(" ").trim()}
        aria-label={open ? "Menyuni yopish" : "Menyuni ochish"}
        aria-expanded={open}
        aria-controls="mobile-menu"
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        type="button"
      >
        <span className={styles.burgerLine} />
      </button>

      {/* Mobile panel */}
      <div
        id="mobile-menu"
        ref={panelRef}
        className={[styles.mobilePanel, open ? styles.mobileOpen : ""].join(" ").trim()}
      >
        <div className={styles.mobileInner}>
          <NavLink to="/about" className={mobileLinkClass}>{t('Biz haqimizda')}</NavLink>

          {!user ? (
            <>
              <div className={styles.mobileDivider} />
              <NavLink to="/login" className={mobileLinkClass}>Kirish</NavLink>
            </>
          ) : (
            <>
              <div className={styles.mobileDivider} />

              <div className={styles.mobileUserBox}>
                <div className={styles.mobileUserRow}>
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="User avatar" className={styles.avatarImgSm} />
                  ) : (
                    <span className={styles.avatarCircleSm}>{initial}</span>
                  )}
                  <span className={styles.mobileUserName}>
                    {user?.username || user?.email || "User"}
                  </span>
                </div>
              </div>

              {!isAdmin && (
                <NavLink to="/profile" className={mobileLinkClass}>{t('profile')}</NavLink>
              )}

              {isTrainer && (
                <>
                  <NavLink to={TRAINER_ADD_ROUTE} className={mobileLinkClass}>{t('addTren')}</NavLink>
                  <NavLink to={TRAINER_LIST_ROUTE} className={mobileLinkClass}>{t('myTren')}</NavLink>
                  <NavLink to={MY_WRITTEN_QUESTIONS_ROUTE} className={mobileLinkClass}>Yozma savollarim</NavLink>
                  <NavLink to={NEW_SUBJECT_ROUTE} className={mobileLinkClass}>{t('Yangi fan')}</NavLink>
                  <NavLink to={MY_SUBJECTS_ROUTE} className={mobileLinkClass}>{t('Mening fanlarim')}</NavLink>
                </>
              )}

              {isAdmin && (
                <>
                  <NavLink to={TRAINER_LIST_ROUTE} className={mobileLinkClass}>{t('Mashqlar')}</NavLink>
                  <NavLink to={MY_WRITTEN_QUESTIONS_ROUTE} className={mobileLinkClass}>{t('Yozma savollar')}</NavLink>
                  <NavLink to={MY_SUBJECTS_ROUTE} className={mobileLinkClass}>{t('Fanlar')}</NavLink>
                  <NavLink to="/user-roles" className={mobileLinkClass}>Foydalanuvchi rollari</NavLink>
                </>
              )}

              <div className={styles.mobileDivider} />

              <button type="button" className={styles.mobileLinkBtn} onClick={handleLogout}>
                {t('Logout')}
              </button>
            </>
          )}
        </div>
      </div>
    </header>
  );
};
